import { useState, useEffect, useMemo } from 'react'
import { useNavigate, Navigate } from 'react-router-dom'
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Container,
  Group,
  Loader,
  Pagination,
  Paper,
  Select,
  Stack,
  Table,
  Tabs,
  Text,
  Title,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { Plus, ChevronRight, ClipboardList, Eye } from 'lucide-react'
import { useAuth } from '../../context/AuthContext'
import {
  assignmentsApi,
  StudentSummaryRow,
  FlatAssignmentRow,
} from '../../api/assignments'

const STATUS_COLOR: Record<string, string> = {
  unassigned: 'gray',
  assigned: 'blue',
  processing: 'grape',
  submitted: 'orange',
  graded: 'green',
  reviewed: 'green',
}

const STATUS_LABELS: Record<string, string> = {
  unassigned: 'Unassigned',
  assigned: 'Assigned',
  processing: 'Processing',
  submitted: 'Submitted',
  graded: 'Graded',
  reviewed: 'Reviewed',
}

const MODE_LABELS: Record<string, string> = {
  learning: 'Learning',
  practice: 'Practice',
  quiz: 'Quiz',
  review: 'Review',
  enhance: 'Enhance',
  meeting: 'Meeting',
  vocabulary: 'Vocabulary',
  evaluate: 'Evaluate',
  mock: 'Mock',
  test: 'Test',
}

const PAGE_SIZE = 10

function formatModeLabel(mode: string) {
  return MODE_LABELS[mode] ?? mode.charAt(0).toUpperCase() + mode.slice(1)
}

function formatDate(d: string | null) {
  if (!d) return '—'
  return new Date(d).toLocaleDateString()
}

function getTutorFlatRowRoute(row: FlatAssignmentRow) {
  if (row.route === '/student/evaluation-result' && row.action_params) {
    return `/tutor/mock/${row.action_params}`
  }
  return `/assignments/${row.assignment_id}`
}

// ── Summary Tab ────────────────────────────────────────────────────────────

function SummaryTab({ onStudentClick }: { onStudentClick: (studentId: string) => void }) {
  const [rows, setRows] = useState<StudentSummaryRow[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    assignmentsApi.summary()
      .then((res) => setRows(res.data))
      .catch(() => notifications.show({ title: 'Error', message: 'Failed to load summary', color: 'red' }))
      .finally(() => setLoading(false))
  }, [])

  if (loading) {
    return (
      <Box style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '40vh' }}>
        <Loader color="eveningSea" />
      </Box>
    )
  }

  if (rows.length === 0) {
    return (
      <Paper shadow="xs" radius="md" p="xl" style={{ textAlign: 'center', background: 'var(--sat-bg)' }}>
        <ClipboardList size={48} color="var(--sat-blush)" style={{ margin: '0 auto 12px' }} />
        <Text fw={600} c="var(--sat-text)" mb={4}>No student data yet</Text>
        <Text size="sm" c="var(--sat-text-soft)">Assign tasks to students to see their progress here.</Text>
      </Paper>
    )
  }

  return (
    <Paper shadow="xs" radius="md" p={0} style={{ overflow: 'hidden' }}>
      <Table highlightOnHover>
        <Table.Thead style={{ background: 'var(--sat-bg)' }}>
          <Table.Tr>
            <Table.Th>Student</Table.Th>
            <Table.Th style={{ textAlign: 'center' }}>Total</Table.Th>
            <Table.Th style={{ textAlign: 'center' }}>Assigned</Table.Th>
            <Table.Th style={{ textAlign: 'center' }}>In Progress</Table.Th>
            <Table.Th style={{ textAlign: 'center' }}>Completed</Table.Th>
            <Table.Th style={{ textAlign: 'center' }}>Progress</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map((r) => {
            const pct = r.total > 0 ? Math.round((r.completed / r.total) * 100) : 0
            return (
            <Table.Tr key={r.student_id}>
              <Table.Td>
                <Text
                  size="sm" fw={500} c="eveningSea"
                  style={{ cursor: 'pointer', textDecoration: 'underline' }}
                  onClick={() => onStudentClick(String(r.student_id))}
                >
                  {r.student_name}
                </Text>
              </Table.Td>
              <Table.Td style={{ textAlign: 'center' }}>
                <Text size="sm" fw={600}>{r.total}</Text>
              </Table.Td>
              <Table.Td style={{ textAlign: 'center' }}>
                <Badge size="sm" color="blue" variant="light">{r.assigned}</Badge>
              </Table.Td>
              <Table.Td style={{ textAlign: 'center' }}>
                <Badge size="sm" color="grape" variant="light">{r.in_progress}</Badge>
              </Table.Td>
              <Table.Td style={{ textAlign: 'center' }}>
                <Badge size="sm" color="green" variant="light">{r.completed}</Badge>
              </Table.Td>
              <Table.Td style={{ textAlign: 'center' }}>
                <Badge size="sm" color={pct === 100 ? 'green' : pct >= 50 ? 'yellow' : 'gray'} variant="light">
                  {pct}%
                </Badge>
              </Table.Td>
            </Table.Tr>
            )
          })}
        </Table.Tbody>
      </Table>
    </Paper>
  )
}

// ── Assignments Tab ────────────────────────────────────────────────────────

function AssignmentsTab({
  onCreateClick,
  filterStudentId,
  setFilterStudentId,
}: {
  onCreateClick: () => void
  filterStudentId: string | null
  setFilterStudentId: (v: string | null) => void
}) {
  const navigate = useNavigate()
  const [rows, setRows] = useState<FlatAssignmentRow[]>([])
  const [loading, setLoading] = useState(true)

  const [filterStatus, setFilterStatus] = useState<string | null>(null)
  const [filterMode, setFilterMode] = useState<string | null>(null)
  const [page, setPage] = useState(1)

  useEffect(() => {
    assignmentsApi.flatRows()
      .then((res) => setRows(res.data))
      .catch(() => notifications.show({ title: 'Error', message: 'Failed to load assignments', color: 'red' }))
      .finally(() => setLoading(false))
  }, [])

  const studentFilterOptions = useMemo(() => {
    const byId = new Map<number, string>()
    for (const r of rows) {
      if (!byId.has(r.student_id)) byId.set(r.student_id, r.student_name)
    }
    return Array.from(byId.entries())
      .sort((x, y) => x[1].localeCompare(y[1]))
      .map(([id, label]) => ({ value: String(id), label }))
  }, [rows])

  const modeFilterOptions = useMemo(() => {
    const modes = new Set<string>()
    for (const r of rows) {
      if (r.assignment_type) modes.add(r.assignment_type)
    }
    return Array.from(modes)
      .sort((a, b) => formatModeLabel(a).localeCompare(formatModeLabel(b)))
      .map((m) => ({ value: m, label: formatModeLabel(m) }))
  }, [rows])

  const statusFilterOptions = useMemo(
    () =>
      (Object.keys(STATUS_COLOR) as (keyof typeof STATUS_COLOR)[]).map((s) => ({
        value: s,
        label: STATUS_LABELS[s] ?? s,
      })),
    [],
  )

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (filterStudentId && r.student_id !== Number(filterStudentId)) return false
      if (filterStatus && r.status !== filterStatus) return false
      if (filterMode && r.assignment_type !== filterMode) return false
      return true
    })
  }, [rows, filterStudentId, filterStatus, filterMode])

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))

  const paginated = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE
    return filtered.slice(start, start + PAGE_SIZE)
  }, [filtered, page])

  useEffect(() => { setPage(1) }, [filterStudentId, filterStatus, filterMode])
  useEffect(() => { if (page > totalPages) setPage(totalPages) }, [page, totalPages])

  if (loading) {
    return (
      <Box style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '40vh' }}>
        <Loader color="eveningSea" />
      </Box>
    )
  }

  return (
    <Stack gap="md">
      <Group gap="sm" wrap="wrap" justify="flex-end" align="flex-end">
        <Select
          placeholder="All students"
          clearable
          searchable
          data={studentFilterOptions}
          value={filterStudentId}
          onChange={setFilterStudentId}
          w={200}
          disabled={studentFilterOptions.length === 0}
        />
        <Select
          placeholder="All statuses"
          clearable
          data={statusFilterOptions}
          value={filterStatus}
          onChange={setFilterStatus}
          w={160}
        />
        <Select
          placeholder="All modes"
          clearable
          searchable
          data={modeFilterOptions}
          value={filterMode}
          onChange={setFilterMode}
          w={180}
          disabled={modeFilterOptions.length === 0}
        />
        <Button
          leftSection={<Plus size={16} />}
          color="grapefruit"
          onClick={onCreateClick}
        >
          Create Assignment
        </Button>
      </Group>

      {rows.length === 0 ? (
        <Paper shadow="xs" radius="md" p="xl" style={{ textAlign: 'center', background: 'var(--sat-bg)' }}>
          <ClipboardList size={48} color="var(--sat-blush)" style={{ margin: '0 auto 12px' }} />
          <Text fw={600} c="var(--sat-text)" mb={4}>No assignments yet</Text>
          <Text size="sm" c="var(--sat-text-soft)" mb={16}>Create your first assignment from the question bank.</Text>
          <Button color="grapefruit" onClick={onCreateClick}>Create Assignment</Button>
        </Paper>
      ) : filtered.length === 0 ? (
        <Paper shadow="xs" radius="md" p="xl" style={{ textAlign: 'center', background: 'var(--sat-bg)' }}>
          <Text fw={600} c="var(--sat-text)" mb={4}>No matching assignments</Text>
          <Text size="sm" c="var(--sat-text-soft)" mb={16}>Try changing or clearing the filters.</Text>
          <Button
            variant="light"
            color="eveningSea"
            onClick={() => {
              setFilterStudentId(null)
              setFilterStatus(null)
              setFilterMode(null)
            }}
          >
            Clear filters
          </Button>
        </Paper>
      ) : (
        <>
          <Paper shadow="xs" radius="md" p={0} style={{ overflow: 'hidden' }}>
            <Table highlightOnHover>
              <Table.Thead style={{ background: 'var(--sat-bg)' }}>
                <Table.Tr>
                  <Table.Th>Title</Table.Th>
                  <Table.Th>Student</Table.Th>
                  <Table.Th>Mode</Table.Th>
                  <Table.Th>Questions</Table.Th>
                  <Table.Th>Due Date</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Score</Table.Th>
                  <Table.Th>Actions</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {paginated.map((r) => (
                  <Table.Tr
                    key={r.assignment_student_id}
                    style={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/assignments/${r.assignment_id}`)}
                  >
                    <Table.Td>
                      <Text size="sm" fw={500} c="var(--sat-text)">{r.title}</Text>
                      {r.description && <Text size="xs" c="var(--sat-text-faint)">{r.description}</Text>}
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c="var(--sat-text)">{r.student_name}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Group gap={6}>
                        <Badge size="sm" color="blue" variant="light">{formatModeLabel(r.assignment_type)}</Badge>
                        <Badge size="sm" color={r.grading_mode === 'auto' ? 'green' : 'violet'} variant="light">
                          {r.grading_mode === 'auto' ? 'Auto' : 'Manual'}
                        </Badge>
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{r.question_count}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c="var(--sat-text-subtle)">{formatDate(r.due_date)}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge size="sm" color={STATUS_COLOR[r.status] || 'gray'} variant="light">
                        {STATUS_LABELS[r.status] ?? r.status}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c="var(--sat-text-subtle)">
                        {r.score !== null ? `${Math.round(r.score)}%` : '—'}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Group justify="flex-end" gap={8} wrap="nowrap">
                        <Button
                          size="xs"
                          color="eveningSea"
                          variant="light"
                          leftSection={<Eye size={14} />}
                          onClick={(e) => {
                            e.stopPropagation()
                            navigate(getTutorFlatRowRoute(r))
                          }}
                        >
                          View
                        </Button>
                        <ActionIcon
                          variant="subtle"
                          color="gray"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation()
                            navigate(`/assignments/${r.assignment_id}`)
                          }}
                        >
                          <ChevronRight size={16} />
                        </ActionIcon>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Paper>
          <Group justify="center">
            <Pagination total={totalPages} value={page} onChange={setPage} />
          </Group>
        </>
      )}
    </Stack>
  )
}

// ── Main component ─────────────────────────────────────────────────────────

export function AssignmentList() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const isTutor = user?.role === 'tutor' || user?.role === 'god'
  const [activeTab, setActiveTab] = useState<string | null>('summary')
  const [filterStudentId, setFilterStudentId] = useState<string | null>(null)

  if (!isTutor) {
    return <Navigate to="/study-plan" replace />
  }

  function handleStudentClick(studentId: string) {
    setFilterStudentId(studentId)
    setActiveTab('assignments')
  }

  return (
    <Container size="lg" py={32}>
      <Group justify="space-between" align="flex-start" mb={24}>
        <Box>
          <Title order={2} c="var(--sat-text)">Assignments</Title>
          <Text size="sm" c="var(--sat-text-soft)">Manage assignments for your students.</Text>
        </Box>
      </Group>

      <Tabs value={activeTab} onChange={setActiveTab} variant="outline">
        <Tabs.List mb="md">
          <Tabs.Tab value="summary">Summary</Tabs.Tab>
          <Tabs.Tab value="assignments">Assignments</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="summary">
          <SummaryTab onStudentClick={handleStudentClick} />
        </Tabs.Panel>

        <Tabs.Panel value="assignments">
          <AssignmentsTab
            onCreateClick={() => navigate('/tutor/assignments/create')}
            filterStudentId={filterStudentId}
            setFilterStudentId={setFilterStudentId}
          />
        </Tabs.Panel>
      </Tabs>
    </Container>
  )
}
